import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { MoneyText } from '@/components/MoneyText';
import { StatusChip } from '@/components/StatusChip';
import { useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CloseIcon, InfoIcon } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { ErrorCodes, isApiError, lineBeforeOpening } from '@/api/errors';
import {
  plannedApi,
  type Occurrence,
  type OccurrenceLine,
  type OccurrenceState,
  type Series,
  type Slice,
  type TargetRequest,
} from '@/api/planned';
import { transactionsApi, type OwnerRegisterLine } from '@/api/transactions';
import { ConfirmButton } from '@/components/ConfirmButton';
import { ConfirmPanel } from '@/components/ConfirmDialog';
import { AllocateDialog } from '@/features/accounts/AllocateDialog';
import { canEditAllocations } from '@/features/accounts/accountAccess';
import { currencyRefusedRows } from '@/features/accounts/allocationErrors';
import {
  formatCivilDate,
  formatMoneyMinor,
  minorToInputText,
  parseMoneyToMinor,
} from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { generateRequestId } from '@/utils/requestId';
import { toCivil } from '@/features/envelope/window';
import { useOwnedEnvelopes, type OwnedEnvelope } from '@/features/accounts/useOwnedEnvelopes';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import { addCivilDays } from '@/shared/servedWindow';
import {
  closeTitle,
  extraLine,
  isOutgoing,
  isPartPaid,
  lineAmount,
  occurrenceLabelValues,
  outstandingLine,
  partLine,
  progressLine,
  shortByLine,
} from './paycheckParts';
import { plannedErrorKey } from './plannedErrors';
import { DialogGuardBody } from '@/components/DialogGuardBody';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { ObjectMenu } from '@/shell/OverflowMenu';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { sameValues } from '@/shared/useDirty';

interface OccurrenceSheetProps {
  occurrence: Occurrence;
  series: Series | null;
  account: Account | null;
  owner: boolean;
  currency: string | null;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
  onEditFollowing: () => void;
  onEditSeries: () => void;
  onOpenSeries: () => void;
  onNotify: (message: string) => void;
  /** Open at the match step (Items to review's "Match another deposit", TMMAPP-0173 item 3). */
  initialView?: 'match' | undefined;
}

/**
 * What the drawer shows (DLG-005, TMMAPP-0097 §C 20): the occurrence, or one second step in its
 * place — "Edit this", Match, a slice change, a paycheck's confirmation (Remove a deposit, That's
 * all; TMMAPP-0173), or the subscribe card. A second step never opens a dialog on top; Esc and
 * Cancel go back to the occurrence, and focus returns to the invoker.
 */
type SheetView = 'detail' | 'editThis' | 'match' | 'slice' | 'confirm' | 'subscribe';

/** The paycheck confirmation on screen (TMMAPP-0173 item 1). */
type PartConfirm = { kind: 'remove'; line: OccurrenceLine; key: string } | { kind: 'close' };

const RESOLVED: ReadonlySet<OccurrenceState> = new Set([
  'MATCHED',
  'POSTED',
  'MISSED',
  'SUPERSEDED',
]);

/** YAML ModifyOccurrenceRequest + TargetRequest names, plus spec `plan[i].*`. */
const OCCURRENCE_TOP_FIELDS = ['amountMinor', 'note', 'occursOn', 'payee'] as const;
const SLICE_PROPS = ['envelopeId', 'amountMinor', 'fixedAmountMinor'] as const;
type OccField = 'amountMinor' | 'note' | 'occursOn' | 'payee';
type SliceField = 'envelopeId' | 'amountMinor';

function occurrenceKnownFields(planCount: number): string[] {
  const indexed: string[] = [];
  for (let i = 0; i < planCount; i += 1) {
    for (const prop of SLICE_PROPS) {
      indexed.push(`plan[${i}].${prop}`, `targets[${i}].${prop}`);
    }
  }
  return [...OCCURRENCE_TOP_FIELDS, ...SLICE_PROPS, ...indexed];
}

/**
 * Occurrence drawer (TMMAPP-0009): Outlook verbs, slice actions, match / unmatch / post-now.
 * Match / unmatch / post-now keep one Idempotency-Key until write and calendar refetch succeed.
 * Every second step is a view of the drawer body (`SheetView`), never a dialog over it.
 */
export function OccurrenceSheet({
  occurrence,
  series,
  account,
  owner,
  currency,
  onClose,
  onChanged,
  onEditFollowing,
  onEditSeries,
  onOpenSeries,
  onNotify,
  initialView,
}: OccurrenceSheetProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { envelopes } = useOwnedEnvelopes();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<OccField, string>>>({});
  const [sliceFieldErrors, setSliceFieldErrors] = useState<Partial<Record<SliceField, string>>>({});
  const [sliceRowErrors, setSliceRowErrors] = useState<
    Record<number, Partial<Record<SliceField, string>>>
  >({});
  const [view, setView] = useState<SheetView>('detail');
  // The view the subscribe card replaced: Cancel and Esc go back to it with its values intact,
  // and its unsaved work stays guarded while the card shows.
  const [subscribeFrom, setSubscribeFrom] = useState<SheetView>('detail');
  const subscribeInvoker = useRef<HTMLElement | null>(null);
  const stepView = view === 'subscribe' ? subscribeFrom : view;
  const editOpen = stepView === 'editThis';
  const matchOpen = stepView === 'match';
  // The control that opened a second step; focus returns to it when the step closes.
  const invoker = useRef<HTMLElement | null>(null);
  const actionsButton = useRef<HTMLButtonElement | null>(null);
  const editTitleId = useId();
  const matchTitleId = useId();
  const sliceTitleId = useId();
  const confirmTitleId = useId();
  const confirmConsequenceId = useId();
  const subscribeTitleId = useId();
  // A paycheck's confirmation (Remove a deposit, That's all) in place of the occurrence.
  const [partConfirm, setPartConfirm] = useState<PartConfirm | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  // "Allocate the extra": the bank line's allocation dialog in place of the drawer (one modal).
  const [allocating, setAllocating] = useState<OwnerRegisterLine | null>(null);
  const [occursOn, setOccursOn] = useState(occurrence.occursOn);
  // Editable text in the locale's separators — `parseMoneyToMinor` reads it back.
  const [amount, setAmount] = useState(
    minorToInputText(Math.abs(occurrence.amountMinor), account?.currency ?? currency, locale),
  );
  const [payee, setPayee] = useState(occurrence.payee ?? '');
  const [note, setNote] = useState('');
  const [candidates, setCandidates] = useState<OwnerRegisterLine[]>([]);
  const [picked, setPicked] = useState<number | null>(null);
  const [sliceDlg, setSliceDlg] = useState<
    | { kind: 'reroute'; slice: Slice; envelopeId: number }
    | { kind: 'amount'; slice: Slice; value: string }
    | { kind: 'add'; envelopeId: number; value: string }
    | null
  >(null);
  // The slice step's opening values: it is dirty once the envelope or the amount differs from them.
  const [sliceStart, setSliceStart] = useState<SliceDlg | null>(null);
  // Errors arrive after a write; each bump asks the open sub-dialog to focus its first error.
  const [errorTick, setErrorTick] = useState(0);
  const bump = () => setErrorTick((n) => n + 1);
  // A write (edit, match, slice) is in flight: its dialog shows the loading state and Esc does
  // not ask the discard question.
  const [writing, setWriting] = useState(false);
  const matchKey = useRef<string | null>(null);
  const unmatchKey = useRef<string | null>(null);
  const postKey = useRef<string | null>(null);
  const initialAmount = minorToInputText(
    Math.abs(occurrence.amountMinor),
    account?.currency ?? currency,
    locale,
  );
  // "Edit this occurrence" is dirty when any field differs from the occurrence it opened on.
  const editDirty =
    editOpen &&
    !sameValues(
      { occursOn, amount, payee, note },
      {
        occursOn: occurrence.occursOn,
        amount: initialAmount,
        payee: occurrence.payee ?? '',
        note: '',
      },
    );
  // The fields start from the occurrence as it is now, so a reopening after a save is never dirty.
  function seedEditFields() {
    setOccursOn(occurrence.occursOn);
    setAmount(initialAmount);
    setPayee(occurrence.payee ?? '');
    setNote('');
  }
  const editGuard = useDialogDirtyGuard(
    editDirty,
    () => {
      seedEditFields();
      closeStep();
    },
    writing,
    errorTick,
    editOpen,
  );
  // Picking a line to match is the work to keep: dirty from the first pick.
  const matchGuard = useDialogDirtyGuard(
    matchOpen && picked !== null,
    () => {
      setPicked(null);
      closeStep();
    },
    writing,
    errorTick,
    matchOpen,
  );
  const sliceDirty =
    sliceDlg !== null &&
    sliceStart !== null &&
    !sameValues(sliceValues(sliceDlg), sliceValues(sliceStart));
  const sliceGuard = useDialogDirtyGuard(
    sliceDirty,
    () => closeStep(),
    writing,
    errorTick,
    stepView === 'slice',
  );
  // The step on screen, or the one under the subscribe card, owns the unsaved-work question.
  const activeGuard =
    stepView === 'editThis'
      ? editGuard
      : stepView === 'match'
        ? matchGuard
        : stepView === 'slice'
          ? sliceGuard
          : null;
  const viewTitleId =
    view === 'editThis'
      ? editTitleId
      : view === 'match'
        ? matchTitleId
        : view === 'slice'
          ? sliceTitleId
          : view === 'confirm'
            ? confirmTitleId
            : view === 'subscribe'
              ? subscribeTitleId
              : undefined;

  function openView(next: Exclude<SheetView, 'detail'>, from: HTMLElement | null) {
    invoker.current = from;
    setView(next);
  }

  function openSlice(next: SliceDlg, from: HTMLElement | null) {
    setSliceDlg(next);
    setSliceStart(next);
    openView('slice', from);
  }

  function closeStep() {
    setSliceDlg(null);
    setSliceStart(null);
    setPartConfirm(null);
    setConfirmError(null);
    setView('detail');
  }

  function openConfirm(next: PartConfirm, from: HTMLElement | null) {
    setConfirmError(null);
    setPartConfirm(next);
    openView('confirm', from);
  }

  function cancelSubscribe() {
    setView(subscribeFrom);
  }

  // A second step's heading takes focus as it replaces the occurrence; back on the occurrence,
  // focus returns to the control that opened the step (or the actions button, after a menu).
  const previousView = useRef<SheetView>('detail');
  useEffect(() => {
    const before = previousView.current;
    previousView.current = view;
    if (view === before) return;
    // Back from the subscribe card: to the control whose write asked for it, else the step's
    // heading or the actions button.
    if (before === 'subscribe') {
      const from = subscribeInvoker.current;
      subscribeInvoker.current = null;
      if (from?.isConnected) {
        from.focus();
        return;
      }
    }
    if (view === 'detail') {
      const target = invoker.current?.isConnected ? invoker.current : actionsButton.current;
      target?.focus();
      return;
    }
    if (view !== 'subscribe' && viewTitleId !== undefined) {
      document.getElementById(viewTitleId)?.focus();
    }
  }, [view, viewTitleId]);
  const posted = occurrence.state;
  const unresolved = posted === 'PLANNED' || posted === 'CANCELLED';
  // A part-paid occurrence is re-planned by its own verbs (close, reopen, unmatch), never "Edit this"
  // (tmm-api answers `OCCURRENCE_RESOLVED`; TMMAPI-0202 G19).
  const canOutlook = (!RESOLVED.has(posted) || posted === 'MISSED') && posted !== 'PARTIAL';
  const missedCarve = posted === 'MISSED';
  const today = toCivil(new Date());
  const bank = account?.currency ?? currency;
  // A paycheck in parts (TMMAPP-0173 item 1): every figure is tmm-api's.
  const partPaid = isPartPaid(occurrence);
  const outgoing = isOutgoing(occurrence.amountMinor);
  const openPartial = posted === 'PARTIAL' && !occurrence.closedShort;
  const lines = occurrence.lines;
  const newestLine = lines.length > 0 ? lines[lines.length - 1] : undefined;

  /** The confirmation's write: Remove one deposit (`unmatch {transactionId}`) or That's all (`close`). */
  async function confirmPart() {
    if (partConfirm === null || writing) return;
    setWriting(true);
    setConfirmError(null);
    try {
      if (partConfirm.kind === 'remove') {
        await plannedApi.unmatch(
          occurrence.seriesId,
          occurrence.originalOn,
          partConfirm.key,
          partConfirm.line.transactionId,
        );
      } else {
        await plannedApi.close(occurrence.seriesId, occurrence.originalOn);
      }
      await afterWrite();
      closeStep();
    } catch (err) {
      if (
        partConfirm.kind === 'remove' &&
        isApiError(err) &&
        err.code === ErrorCodes.IDEMPOTENCY_CONFLICT
      ) {
        setPartConfirm({ ...partConfirm, key: generateRequestId() });
      }
      if (!handleWriteError(err)) {
        setConfirmError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
      }
    } finally {
      setWriting(false);
    }
  }

  /** "Allocate the extra": the allocation dialog for the bank line that holds it, the newest one. */
  async function allocateExtra() {
    if (account === null || newestLine === undefined) return;
    setError(null);
    try {
      setAllocating(await transactionsApi.get(account.id, newestLine.transactionId));
    } catch (err) {
      setError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
    }
  }

  function seriesTargetIdFor(slice: Slice): number | undefined {
    return series?.targets.find((t) => t.envelopeId === slice.envelopeId)?.id;
  }

  async function afterWrite() {
    await onChanged();
  }

  function applyOccurrenceValidation(err: unknown) {
    const next: Partial<Record<OccField, string>> = {};
    const sliceNext: Partial<Record<SliceField, string>> = {};
    const rowNext: Record<number, Partial<Record<SliceField, string>>> = {};
    const openSliceRef =
      sliceDlg !== null && sliceDlg.kind !== 'add' ? sliceDlg.slice.sliceRef : null;
    const result = applyBackendFields(
      err,
      occurrenceKnownFields(occurrence.slices.length),
      (field, message) => {
        const planMatch = /^(?:plan|targets)\[(\d+)\]\.(.+)$/.exec(field);
        if (planMatch !== null) {
          const idx = Number(planMatch[1]);
          const prop = planMatch[2];
          const slot = rowNext[idx] ?? {};
          if (prop === 'envelopeId') slot.envelopeId = message;
          if (prop === 'amountMinor' || prop === 'fixedAmountMinor') slot.amountMinor = message;
          rowNext[idx] = slot;
          const slice = occurrence.slices[idx];
          if (slice !== undefined && (openSliceRef === null || slice.sliceRef === openSliceRef)) {
            if (prop === 'envelopeId') sliceNext.envelopeId = message;
            if (prop === 'amountMinor' || prop === 'fixedAmountMinor')
              sliceNext.amountMinor = message;
          }
          return;
        }
        if (field === 'occursOn' || field === 'payee' || field === 'note') {
          next[field] = message;
          return;
        }
        if (field === 'amountMinor' || field === 'fixedAmountMinor') {
          if (sliceDlg !== null) sliceNext.amountMinor = message;
          else next.amountMinor = message;
          return;
        }
        if (field === 'envelopeId' && sliceDlg !== null) {
          sliceNext.envelopeId = message;
        }
      },
      t,
    );
    setFieldErrors(next);
    setSliceFieldErrors(sliceNext);
    setSliceRowErrors(rowNext);
    setError(validationAlert(result, err, t, t('planned.errorInvalid')));
    bump();
  }

  function handleWriteError(err: unknown): boolean {
    if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
      // The subscribe card replaces the view that asked (TMMAPP-0097 §C 25); that view's values
      // stay, and Cancel or Esc returns to it.
      setSubscribeFrom(view);
      const active = document.activeElement;
      subscribeInvoker.current = active instanceof HTMLElement ? active : null;
      // An occurrence-level write: an earlier step's invoker is stale.
      if (view === 'detail') invoker.current = null;
      setView('subscribe');
      return true;
    }
    if (isApiError(err) && err.code === ErrorCodes.VALIDATION_FAILED) {
      applyOccurrenceValidation(err);
      return true;
    }
    return false;
  }

  /**
   * A part into a virtual account in another currency (TMMAPI-0205): marks the sheet's parts bound
   * there and the slice step's own part (its envelope, or its amount on Change amount, which has no
   * envelope field). The dialog-level sentence stays as the summary and the fallback.
   */
  function markCurrencyRefusal(err: unknown) {
    const message = t('allocations.errorCurrencyMismatch');
    const virtualAccountOf = (envelopeId: number) =>
      envelopes.find((v) => v.id === envelopeId)?.virtualAccountId;
    const rows = currencyRefusedRows(
      err,
      occurrence.slices.map((slice, index) => ({
        key: String(index),
        virtualAccountId: virtualAccountOf(slice.envelopeId),
      })),
    );
    if (rows.length > 0) {
      setSliceRowErrors(
        Object.fromEntries(rows.map((key) => [Number(key), { envelopeId: message }])),
      );
    }
    if (sliceDlg === null) return;
    const stepEnvelopeId =
      sliceDlg.kind === 'amount' ? sliceDlg.slice.envelopeId : sliceDlg.envelopeId;
    const step = currencyRefusedRows(err, [
      { key: 'step', virtualAccountId: virtualAccountOf(stepEnvelopeId) },
    ]);
    if (step.length > 0) {
      setSliceFieldErrors(
        sliceDlg.kind === 'amount' ? { amountMinor: message } : { envelopeId: message },
      );
    }
  }

  async function run(fn: () => Promise<void>) {
    setError(null);
    setFieldErrors({});
    setSliceFieldErrors({});
    setSliceRowErrors({});
    setWriting(true);
    try {
      await fn();
      await afterWrite();
    } catch (err) {
      if (!handleWriteError(err)) {
        markCurrencyRefusal(err);
        setError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
        bump();
      }
    } finally {
      setWriting(false);
    }
  }

  async function handleMatch() {
    if (picked === null || writing) return;
    if (matchKey.current === null) matchKey.current = generateRequestId();
    const key = matchKey.current;
    setWriting(true);
    try {
      await plannedApi.match(occurrence.seriesId, occurrence.originalOn, picked, key);
      await afterWrite();
      matchKey.current = null;
      closeStep();
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.IDEMPOTENCY_CONFLICT) {
        matchKey.current = generateRequestId();
      }
      if (isApiError(err) && err.code === ErrorCodes.MATCH_AMBIGUOUS) {
        setError(t('planned.matchAmbiguous'));
        bump();
        return;
      }
      if (!handleWriteError(err)) {
        setError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
        bump();
      }
    } finally {
      setWriting(false);
    }
  }

  async function openMatch(opener: HTMLElement | null) {
    if (account === null) return;
    const window = series?.match.windowDays ?? 5;
    const from = addCivilDays(occurrence.occursOn, -window);
    const to = addCivilDays(occurrence.occursOn, window);
    const page = await transactionsApi.register(account.id, {
      from,
      to,
      unallocatedOnly: true,
    });
    setCandidates(page.items);
    setPicked(null);
    setError(null);
    openView('match', opener);
  }

  // Opened for "Match another deposit": the match step reads its candidates once, on mount.
  const startedAtMatch = useRef(false);
  useEffect(() => {
    if (initialView !== 'match' || startedAtMatch.current) return;
    startedAtMatch.current = true;
    void openMatch(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on mount
  }, []);

  const differs = occurrence.differsFromSeries;

  const detail = (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h2">{occurrence.payee ?? series?.name}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {formatCivilDate(occurrence.occursOn, locale)}
            {occurrence.occursOn !== occurrence.originalOn &&
              ` · ${t('planned.originalOn', { date: formatCivilDate(occurrence.originalOn, locale) })}`}
          </Typography>
          <MoneyText minor={occurrence.amountMinor} currency={bank} />
          <Stack direction="row" sx={{ gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            <StatusChip
              domain="occurrence"
              status={occurrence.state}
              values={occurrenceLabelValues(occurrence.amountMinor)}
            />
            {occurrence.pending === true && (
              <Tooltip title={t('planned.pendingFlag')}>
                <Box
                  component="span"
                  role="img"
                  aria-label={t('planned.pendingFlag')}
                  sx={{ display: 'inline-flex' }}
                >
                  <InfoIcon />
                </Box>
              </Tooltip>
            )}
            {differs?.date === true && <Chip size="small" label={t('planned.occursOn')} />}
            {differs?.amount === true && <Chip size="small" label={t('planned.amount')} />}
            {differs?.slices === true && <Chip size="small" label={t('planned.targets')} />}
          </Stack>
          {series !== null && (
            <Button size="small" onClick={onOpenSeries}>
              {t('planned.partOfSeries', { name: series.name })}
            </Button>
          )}
        </Box>
        <IconButton aria-label={t('common.close')} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      {/* A step shows its own error; the hidden occurrence underneath does not repeat it. */}
      {error !== null && view === 'detail' && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {partPaid && (
        <Box data-paycheck-progress="" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {progressLine(t, occurrence, bank, locale)}
          </Typography>
          {outstandingLine(t, occurrence, bank, locale) !== null && (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
            >
              {outstandingLine(t, occurrence, bank, locale)}
            </Typography>
          )}
        </Box>
      )}
      {occurrence.extraMinor !== null && (
        <Alert
          severity="info"
          data-paycheck-extra=""
          sx={{ mb: 1.5 }}
          action={
            owner && account !== null && newestLine !== undefined ? (
              <Button color="inherit" size="small" onClick={() => void allocateExtra()}>
                {t('planned.parts.allocateExtra')}
              </Button>
            ) : undefined
          }
        >
          {extraLine(t, occurrence, bank, locale)}
        </Alert>
      )}
      <Table size="small" aria-label={t('planned.targets')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('planned.pickEnvelope')}</TableCell>
            <TableCell align="right">{t('planned.amount')}</TableCell>
            {unresolved && owner && <ActionsHeaderCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {occurrence.slices.map((slice, sliceIndex) => (
            <TableRow key={slice.sliceRef}>
              <TableCell>
                {slice.envelopeName}
                {slice.virtualAccountLabel !== null && (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    {slice.virtualAccountLabel}
                  </Typography>
                )}
                {sliceRowErrors[sliceIndex]?.envelopeId !== undefined && (
                  <FormHelperText error>{sliceRowErrors[sliceIndex]?.envelopeId}</FormHelperText>
                )}
              </TableCell>
              <TableCell align="right">
                <MoneyText minor={slice.amountMinor} currency={bank} />
                {partLine(t, slice, bank, locale) !== null && (
                  <Typography
                    variant="caption"
                    data-part-progress=""
                    sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {partLine(t, slice, bank, locale)}
                  </Typography>
                )}
                {occurrence.closedShort && shortByLine(t, slice, bank, locale) !== null && (
                  <Typography
                    variant="caption"
                    data-part-short=""
                    sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {shortByLine(t, slice, bank, locale)}
                  </Typography>
                )}
                {sliceRowErrors[sliceIndex]?.amountMinor !== undefined && (
                  <FormHelperText error>{sliceRowErrors[sliceIndex]?.amountMinor}</FormHelperText>
                )}
              </TableCell>
              {unresolved && owner && (
                <TableCell>
                  <Button
                    size="small"
                    onClick={(event) =>
                      openSlice(
                        { kind: 'reroute', slice, envelopeId: slice.envelopeId },
                        event.currentTarget,
                      )
                    }
                  >
                    {t('planned.sliceReroute')}
                  </Button>
                  <Button
                    size="small"
                    onClick={(event) =>
                      openSlice(
                        {
                          kind: 'amount',
                          slice,
                          value: minorToInputText(Math.abs(slice.amountMinor), bank, locale),
                        },
                        event.currentTarget,
                      )
                    }
                  >
                    {t('planned.sliceChangeAmount')}
                  </Button>
                  <ConfirmButton
                    size="small"
                    onConfirm={() =>
                      void run(async () => {
                        await plannedApi.cancelSlice(
                          occurrence.seriesId,
                          occurrence.originalOn,
                          slice.sliceRef,
                        );
                      })
                    }
                  >
                    {t('planned.sliceCancel')}
                  </ConfirmButton>
                </TableCell>
              )}
            </TableRow>
          ))}
          <TableRow>
            <TableCell>{t('accounts.colRemainder')}</TableCell>
            <TableCell align="right">
              <MoneyText minor={occurrence.remainderMinor} currency={bank} />
            </TableCell>
            {unresolved && owner && <TableCell />}
          </TableRow>
        </TableBody>
      </Table>
      {unresolved && owner && (
        <Stack direction="row" sx={{ gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            onClick={(event) =>
              openSlice(
                { kind: 'add', envelopeId: envelopes[0]?.id ?? 0, value: '' },
                event.currentTarget,
              )
            }
          >
            {t('planned.sliceAdd')}
          </Button>
          <Button
            size="small"
            onClick={() =>
              void run(async () => {
                await plannedApi.restoreSlices(occurrence.seriesId, occurrence.originalOn);
              })
            }
          >
            {t('planned.slicesRestore')}
          </Button>
        </Stack>
      )}
      {/* The bank lines a paycheck in parts holds, oldest first, each with Remove (item 1). */}
      {(partPaid || lines.length > 1) && (
        <Box data-paycheck-lines="" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" component="h3">
            {outgoing ? t('planned.parts.linesPaid') : t('planned.parts.linesReceived')}
          </Typography>
          <Table
            size="small"
            aria-label={outgoing ? t('planned.parts.linesPaid') : t('planned.parts.linesReceived')}
          >
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.transactionId}>
                  <TableCell>{formatCivilDate(line.postedOn, locale)}</TableCell>
                  <TableCell align="right">
                    <MoneyText minor={line.amountMinor} currency={bank} />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      aria-label={t('planned.parts.removeLine', {
                        amount: lineAmount(line.amountMinor, bank, locale),
                        date: formatCivilDate(line.postedOn, locale),
                      })}
                      onClick={(event) =>
                        openConfirm(
                          { kind: 'remove', line, key: generateRequestId() },
                          event.currentTarget,
                        )
                      }
                    >
                      {t('planned.parts.remove')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Stack direction="row" sx={{ gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Button
          ref={actionsButton}
          aria-label={t('planned.occurrenceActions')}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
        >
          {t('planned.occurrenceActions')}
        </Button>
        {(posted === 'PLANNED' || posted === 'MISSED') && (
          <Button onClick={(event) => void openMatch(event.currentTarget)}>
            {t('planned.match')}
          </Button>
        )}
        {openPartial && (
          <>
            <Button onClick={(event) => void openMatch(event.currentTarget)}>
              {outgoing ? t('planned.parts.matchAnotherPaid') : t('planned.parts.matchAnother')}
            </Button>
            <Button
              variant="outlined"
              onClick={(event) => openConfirm({ kind: 'close' }, event.currentTarget)}
            >
              {t('planned.parts.thatsAll')}
            </Button>
          </>
        )}
        {occurrence.closedShort && (
          <Button
            variant="outlined"
            onClick={() =>
              void run(async () => {
                await plannedApi.reopen(occurrence.seriesId, occurrence.originalOn);
              })
            }
          >
            {t('planned.parts.reopen')}
          </Button>
        )}
        {(posted === 'MATCHED' || posted === 'POSTED' || posted === 'PARTIAL') && (
          <Button
            onClick={() =>
              void (async () => {
                if (unmatchKey.current === null) unmatchKey.current = generateRequestId();
                const key = unmatchKey.current;
                setError(null);
                try {
                  await plannedApi.unmatch(occurrence.seriesId, occurrence.originalOn, key);
                  await afterWrite();
                  unmatchKey.current = null;
                } catch (err) {
                  if (isApiError(err) && err.code === ErrorCodes.IDEMPOTENCY_CONFLICT) {
                    unmatchKey.current = generateRequestId();
                  }
                  if (!handleWriteError(err)) {
                    setError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
                  }
                }
              })()
            }
          >
            {lines.length > 1 ? t('planned.parts.unmatchAll') : t('planned.unmatch')}
          </Button>
        )}
        {series?.fulfilment === 'AUTO_POST' && owner && posted === 'PLANNED' && (
          <Button
            onClick={() =>
              void (async () => {
                if (postKey.current === null) postKey.current = generateRequestId();
                const key = postKey.current;
                setError(null);
                try {
                  await plannedApi.postNow(occurrence.seriesId, occurrence.originalOn, key);
                  await afterWrite();
                  postKey.current = null;
                } catch (err) {
                  if (isApiError(err) && err.code === ErrorCodes.IDEMPOTENCY_CONFLICT) {
                    postKey.current = generateRequestId();
                  }
                  const beforeOpening = lineBeforeOpening(err);
                  if (beforeOpening !== null) {
                    // Dated on or before the opening date (TMMAPI-0198; TMMAPP-0168 item 1).
                    setError(
                      beforeOpening.openingOn !== null
                        ? t('planned.postBeforeOpeningOn', {
                            date: formatCivilDate(beforeOpening.openingOn, locale),
                          })
                        : t('planned.postBeforeOpening'),
                    );
                  } else if (!handleWriteError(err)) {
                    setError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
                  }
                }
              })()
            }
          >
            {t('planned.postNow')}
          </Button>
        )}
      </Stack>
      <ObjectMenu
        title={occurrence.payee ?? series?.name ?? ''}
        anchorEl={menuAnchor}
        open={menuAnchor !== null}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          disabled={!canOutlook && posted !== 'PLANNED'}
          onClick={() => {
            setMenuAnchor(null);
            if (RESOLVED.has(posted) && posted !== 'MISSED') return;
            seedEditFields();
            openView('editThis', actionsButton.current);
          }}
        >
          {t('planned.editThis')}
        </MenuItem>
        <MenuItem
          disabled={posted !== 'PLANNED' && posted !== 'MISSED'}
          onClick={() => {
            setMenuAnchor(null);
            void run(async () => {
              await plannedApi.cancelOccurrence(occurrence.seriesId, occurrence.originalOn);
            });
          }}
        >
          {t('planned.cancelThis')}
        </MenuItem>
        <MenuItem
          disabled={posted !== 'CANCELLED'}
          onClick={() => {
            setMenuAnchor(null);
            void run(async () => {
              await plannedApi.restoreOccurrence(occurrence.seriesId, occurrence.originalOn);
            });
          }}
        >
          {t('planned.restoreThis')}
        </MenuItem>
        <MenuItem
          disabled={posted !== 'PLANNED'}
          onClick={() => {
            setMenuAnchor(null);
            onEditFollowing();
          }}
        >
          {t('planned.editFollowing')}
        </MenuItem>
        <MenuItem
          disabled={posted !== 'PLANNED'}
          onClick={() => {
            setMenuAnchor(null);
            onEditSeries();
          }}
        >
          {t('planned.editSeries')}
        </MenuItem>
        <MenuItem
          disabled={posted !== 'PLANNED' || occurrence.originalOn <= today}
          onClick={() => {
            setMenuAnchor(null);
            if (occurrence.originalOn <= today) {
              setError(t('planned.endFromPast'));
              return;
            }
            void run(async () => {
              await plannedApi.endFrom(occurrence.seriesId, { fromDate: occurrence.originalOn });
            });
          }}
        >
          {t('planned.cancelFollowing')}
        </MenuItem>
        <MenuItem
          disabled={posted !== 'PLANNED'}
          onClick={() => {
            setMenuAnchor(null);
            void run(async () => {
              await plannedApi.cancelSeries(occurrence.seriesId);
            });
          }}
        >
          {t('planned.cancelSeries')}
        </MenuItem>
      </ObjectMenu>
      {RESOLVED.has(posted) && posted !== 'MISSED' && posted !== 'CANCELLED' && (
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
          {t('planned.resolvedNoEdit')}
        </Typography>
      )}
    </Box>
  );

  const editStep = (
    <>
      <DialogTitle id={editTitleId} tabIndex={-1}>
        {t('planned.editThisTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <TextField
            type="date"
            label={t('planned.occursOn')}
            value={occursOn}
            onChange={(event) => setOccursOn(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            error={fieldErrors.occursOn !== undefined}
            helperText={fieldErrors.occursOn}
          />
          {!missedCarve && (
            <TextField
              label={t('planned.amount')}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              error={fieldErrors.amountMinor !== undefined}
              helperText={fieldErrors.amountMinor}
            />
          )}
          {!missedCarve && (
            <TextField
              label={t('planned.payee')}
              value={payee}
              onChange={(event) => setPayee(event.target.value)}
              error={fieldErrors.payee !== undefined}
              helperText={fieldErrors.payee}
            />
          )}
          {!missedCarve && (
            <TextField
              label={t('planned.note')}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              error={fieldErrors.note !== undefined}
              helperText={fieldErrors.note}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={editGuard.requestClose} disabled={writing}>
          {t('common.cancel')}
        </Button>
        <SubmitButton
          loading={writing}
          onClick={() => {
            void run(async () => {
              if (missedCarve) {
                await plannedApi.modifyOccurrence(occurrence.seriesId, occurrence.originalOn, {
                  occursOn,
                });
              } else {
                const mag = parseMoneyToMinor(amount, bank, locale);
                await plannedApi.modifyOccurrence(occurrence.seriesId, occurrence.originalOn, {
                  occursOn,
                  ...(mag !== null
                    ? {
                        amountMinor: occurrence.amountMinor < 0 ? -Math.abs(mag) : Math.abs(mag),
                      }
                    : {}),
                  ...(payee.trim().length > 0 ? { payee: payee.trim() } : {}),
                  ...(note.trim().length > 0 ? { note: note.trim() } : {}),
                });
              }
              // Saved: the fields (the note included) start over, so reopening is not dirty.
              setNote('');
              closeStep();
            });
          }}
        >
          {t('common.save')}
        </SubmitButton>
      </DialogActions>
    </>
  );

  const matchStep = (
    <>
      <DialogTitle id={matchTitleId} tabIndex={-1}>
        {t('planned.matchTitle')}
      </DialogTitle>
      <DialogContent>
        {error !== null && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {candidates.length === 0 ? (
          <Typography>{t('planned.matchEmpty')}</Typography>
        ) : (
          <FormControl>
            <RadioGroup
              value={picked === null ? '' : String(picked)}
              onChange={(_event, value) => {
                matchKey.current = null;
                setPicked(Number(value));
              }}
            >
              {candidates.map((line) => (
                <FormControlLabel
                  key={line.id}
                  value={String(line.id)}
                  control={<Radio />}
                  label={`${formatCivilDate(line.postedOn, locale)} · ${line.payee} · ${formatMoneyMinor(line.amountMinor, bank, locale)}`}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={matchGuard.requestClose} disabled={writing}>
          {t('common.cancel')}
        </Button>
        <SubmitButton
          loading={writing}
          disabled={picked === null}
          onClick={() => void handleMatch()}
        >
          {t('planned.match')}
        </SubmitButton>
      </DialogActions>
    </>
  );

  const sliceStep =
    sliceDlg === null ? null : (
      <SliceStep
        dlg={sliceDlg}
        setDlg={setSliceDlg}
        titleId={sliceTitleId}
        envelopes={envelopes}
        remainderMinor={occurrence.remainderMinor}
        currency={bank}
        locale={locale}
        alert={error}
        busy={writing}
        fieldErrors={sliceFieldErrors}
        onCancel={sliceGuard.requestClose}
        onSubmit={(body) =>
          void run(async () => {
            await plannedApi.upsertSlice(occurrence.seriesId, occurrence.originalOn, body);
            closeStep();
          })
        }
        seriesTargetId={sliceDlg.kind === 'add' ? undefined : seriesTargetIdFor(sliceDlg.slice)}
      />
    );

  // A paycheck's confirmation in place of the occurrence (the drawer is the one modal, DLG-005).
  const confirmStep =
    partConfirm === null ? null : (
      <ConfirmPanel
        titleId={confirmTitleId}
        consequenceId={confirmConsequenceId}
        focusHeading
        title={
          partConfirm.kind === 'remove'
            ? outgoing
              ? t('planned.parts.removeTitlePaid')
              : t('planned.parts.removeTitle')
            : closeTitle(t, occurrence, bank, locale)
        }
        consequence={
          partConfirm.kind === 'remove'
            ? outgoing
              ? t('planned.parts.removeConsequencePaid')
              : t('planned.parts.removeConsequence')
            : t('planned.parts.closeConsequence')
        }
        confirmLabel={
          partConfirm.kind === 'remove' ? t('planned.parts.remove') : t('planned.parts.thatsAll')
        }
        destructive={partConfirm.kind === 'remove'}
        busy={writing}
        error={confirmError}
        onConfirm={() => void confirmPart()}
        onCancel={() => {
          if (!writing) closeStep();
        }}
      />
    );

  const subscribeStep = (
    <Box sx={{ p: 2 }}>
      <SubscribeInPlace titleId={subscribeTitleId} focusTitle onClose={onClose} />
      <DialogActions>
        <Button onClick={cancelSubscribe}>{t('common.cancel')}</Button>
      </DialogActions>
    </Box>
  );

  const step =
    view === 'editThis'
      ? editStep
      : view === 'match'
        ? matchStep
        : view === 'slice'
          ? sliceStep
          : view === 'confirm'
            ? confirmStep
            : view === 'subscribe'
              ? subscribeStep
              : null;

  return (
    <>
      <Drawer
        anchor="right"
        // "Allocate the extra" shows the allocation dialog in its place, then returns to it.
        open={allocating === null}
        // Above the fixed app bar, as TransactionDrawer is, so a step's focused heading is not covered.
        sx={{ zIndex: theme.zIndex.modal }}
        // Esc on a second step goes back to the occurrence (through its unsaved-work guard); on the
        // subscribe card, back to the view it replaced (or Keep editing, while that view asks).
        onClose={() => {
          if (view === 'subscribe' && activeGuard?.discarding !== true) cancelSubscribe();
          else if (view === 'confirm') {
            if (!writing) closeStep();
          } else if (activeGuard !== null) activeGuard.requestClose();
          else onClose();
        }}
        slotProps={{
          paper: {
            sx: { width: fullScreen ? '100%' : 480, maxWidth: '100%' },
            // The drawer is the one modal: it takes the name of the step it shows.
            ...(activeGuard?.discarding === true
              ? {
                  'aria-labelledby': activeGuard.titleId,
                  'aria-describedby': activeGuard.consequenceId,
                }
              : viewTitleId !== undefined
                ? { 'aria-labelledby': viewTitleId }
                : {}),
          },
        }}
      >
        {activeGuard !== null ? (
          <DialogGuardBody guard={activeGuard}>{step}</DialogGuardBody>
        ) : (
          step
        )}
        {/* The occurrence stays mounted under a step (hidden, out of the tab order and the
          accessibility tree), so focus can return to the control that opened the step. */}
        <Box sx={{ display: view === 'detail' ? 'contents' : 'none' }}>{detail}</Box>
      </Drawer>
      {allocating !== null && account !== null && (
        <AllocateDialog
          account={account}
          line={allocating}
          editor={owner && canEditAllocations(account)}
          owner={owner}
          onClose={() => setAllocating(null)}
          onChanged={async (line) => {
            setAllocating((prev) => (prev !== null ? line : prev));
            await afterWrite();
          }}
          onNotify={onNotify}
        />
      )}
    </>
  );
}

function sliceValues(dlg: SliceDlg) {
  return {
    envelopeId: dlg.kind === 'amount' ? null : dlg.envelopeId,
    value: dlg.kind === 'reroute' ? null : dlg.value,
  };
}

type SliceDlg =
  | { kind: 'reroute'; slice: Slice; envelopeId: number }
  | { kind: 'amount'; slice: Slice; value: string }
  | { kind: 'add'; envelopeId: number; value: string };

/**
 * The slice step (reroute / change amount / add) in place of the occurrence. The host holds its
 * unsaved-work guard (dirty from the opening envelope and amount) and routes Cancel and Esc through it.
 */
function SliceStep({
  dlg,
  setDlg,
  titleId,
  envelopes,
  remainderMinor,
  currency,
  locale,
  alert,
  busy,
  fieldErrors,
  onCancel,
  onSubmit,
  seriesTargetId,
}: {
  dlg: SliceDlg;
  setDlg: (next: SliceDlg | null) => void;
  titleId: string;
  envelopes: OwnedEnvelope[];
  remainderMinor: number;
  /** The account's currency: a part's amount and a target's fixed amount are in it. */
  currency: string | null;
  locale: string;
  alert: string | null;
  /** The slice write is in flight: Save shows its loading state and the step is not dismissible. */
  busy: boolean;
  fieldErrors: Partial<Record<SliceField, string>>;
  onCancel: () => void;
  onSubmit: (body: TargetRequest) => void;
  seriesTargetId: number | undefined;
}) {
  const { t } = useTranslation();
  const dest =
    dlg.kind === 'reroute' || dlg.kind === 'add'
      ? envelopes.find((v) => v.id === dlg.envelopeId)
      : undefined;
  const src =
    dlg.kind === 'reroute' ? envelopes.find((v) => v.id === dlg.slice.envelopeId) : undefined;
  const leavesView =
    dlg.kind === 'reroute' &&
    dest !== undefined &&
    src !== undefined &&
    dest.virtualAccountId !== src.virtualAccountId;
  const addMag = dlg.kind === 'add' ? parseMoneyToMinor(dlg.value, currency, locale) : null;
  const addExceeds =
    dlg.kind === 'add' && addMag !== null && Math.abs(addMag) > Math.abs(remainderMinor);

  function submit() {
    if (dlg.kind === 'reroute') {
      const body: TargetRequest = {
        envelopeId: dlg.envelopeId,
        mode: 'FIXED',
        fixedAmountMinor: dlg.slice.amountMinor,
      };
      if (seriesTargetId !== undefined) body.seriesTargetId = seriesTargetId;
      onSubmit(body);
      return;
    }
    if (dlg.kind === 'amount') {
      const mag = parseMoneyToMinor(dlg.value, currency, locale);
      if (mag === null) return;
      const body: TargetRequest = {
        envelopeId: dlg.slice.envelopeId,
        mode: 'FIXED',
        fixedAmountMinor: dlg.slice.amountMinor < 0 ? -Math.abs(mag) : Math.abs(mag),
      };
      if (seriesTargetId !== undefined) body.seriesTargetId = seriesTargetId;
      onSubmit(body);
      return;
    }
    const mag = parseMoneyToMinor(dlg.value, currency, locale);
    if (mag === null || addExceeds) return;
    onSubmit({
      envelopeId: dlg.envelopeId,
      mode: 'FIXED',
      fixedAmountMinor: remainderMinor < 0 ? -Math.abs(mag) : Math.abs(mag),
    });
  }

  return (
    <>
      <DialogTitle id={titleId} tabIndex={-1}>
        {dlg.kind === 'reroute'
          ? t('planned.rerouteTitle')
          : dlg.kind === 'amount'
            ? t('planned.changeAmountTitle')
            : t('planned.addSliceTitle')}
      </DialogTitle>
      <DialogContent>
        {alert !== null && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {alert}
          </Alert>
        )}
        {addExceeds && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {t('planned.planExceeds')}
          </Alert>
        )}
        {leavesView && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t('planned.leavesView')}
          </Alert>
        )}
        {(dlg.kind === 'reroute' || dlg.kind === 'add') && (
          <FormControl fullWidth sx={{ mt: 1 }} error={fieldErrors.envelopeId !== undefined}>
            <InputLabel id="slice-envelope">{t('planned.pickEnvelope')}</InputLabel>
            <Select
              labelId="slice-envelope"
              label={t('planned.pickEnvelope')}
              value={dlg.envelopeId}
              onChange={(event) => setDlg({ ...dlg, envelopeId: Number(event.target.value) })}
            >
              {envelopes.map((envelope) => (
                <MenuItem key={envelope.id} value={envelope.id}>
                  {envelope.name}
                </MenuItem>
              ))}
            </Select>
            {fieldErrors.envelopeId !== undefined && (
              <FormHelperText>{fieldErrors.envelopeId}</FormHelperText>
            )}
          </FormControl>
        )}
        {(dlg.kind === 'amount' || dlg.kind === 'add') && (
          <TextField
            label={t('planned.amount')}
            value={dlg.value}
            onChange={(event) => setDlg({ ...dlg, value: event.target.value })}
            fullWidth
            sx={{ mt: 1 }}
            error={fieldErrors.amountMinor !== undefined}
            helperText={fieldErrors.amountMinor}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </Button>
        {leavesView ? (
          <ConfirmButton variant="contained" disabled={busy} onConfirm={submit}>
            {t('planned.leavesView')}
          </ConfirmButton>
        ) : (
          <SubmitButton loading={busy} disabled={addExceeds} onClick={submit}>
            {t('common.save')}
          </SubmitButton>
        )}
      </DialogActions>
    </>
  );
}
